import { useState } from 'react';
import {
  AGREED_TIES,
  DEFAULT_FAMILY,
  CLOSENESS_MAX,
  NEEDS,
  SOCIAL_ACTIONS,
  SOCIAL_ACTION_KEYS,
  TIE_LABELS,
  type AgreedTie,
  type KinKind,
  type NeedKey,
  type PersonAction,
  type PersonView,
  type SocialActionKey,
} from '@london-life/contracts';
import { lookColours } from '@london-life/game/appearance';
import { useCatalogue } from '../lib/content';

// a small picture of another player: their skin tone, hair and outfit colours, and their initial
export function PersonPicture({ person }: { person: Pick<PersonView, 'firstName' | 'look'> }) {
  const colours = lookColours(person.look);
  return (
    <span
      className="person-picture"
      aria-hidden="true"
      style={{ background: colours.skin, borderColor: colours.outfit, boxShadow: `inset 0 6px 0 ${colours.hair}` }}
    >
      {person.firstName.slice(0, 1).toUpperCase()}
    </span>
  );
}

const effectsOf = (effects: Partial<Record<NeedKey, number>>) =>
  (Object.entries(effects) as [NeedKey, number][])
    .map(([key, value]) => `${NEEDS[key].icon} ${value > 0 ? '+' : ''}${value}`)
    .join(' ');

// The player card (v1): what you can do with one person. Social actions need both of you in the same place;
// partner and family are asked and answered (family says how: sibling, cousin…, as Lagos Life's kin); blocking
// hides you from each other. Every button asks the server.
export function PersonCard({
  person,
  busy,
  onAct,
  compact = false,
  onChat,
}: {
  person: PersonView;
  busy: boolean;
  onAct: (lifeId: string, body: PersonAction) => Promise<unknown>;
  // the "Who's here" list: social actions first, less about the tie
  compact?: boolean;
  // open a direct chat with them
  onChat?: (lifeId: string) => void;
}) {
  const [confirm, setConfirm] = useState<'block' | 'end' | null>(null);
  // asking to be family: the kind chosen first
  const [choosingKin, setChoosingKin] = useState(false);
  const kinLabels = (useCatalogue()?.family ?? DEFAULT_FAMILY).kin;
  const kinOf = (kind: KinKind | null) => kinLabels.find((item) => item.kind === kind);
  const agreed = person.tie === 'partner' || person.tie === 'family' ? (person.tie as AgreedTie) : null;
  const kin = agreed === 'family' ? kinOf(person.kin) : undefined;
  const pendingKin = person.pending?.tie === 'family' ? kinOf(person.pending.kin) : undefined;
  const act = (body: PersonAction) => void onAct(person.lifeId, body);
  return (
    <li className={`person-card${person.here ? ' here' : ''}${person.blocked ? ' blocked' : ''}`}>
      <div className="person-head">
        <PersonPicture person={person} />
        <div className="person-who">
          <b>
            {person.firstName}
            {person.username && <small>@{person.username}</small>}
          </b>
          <span className="person-tie">
            {kin
              ? `${kin.emoji} ${kin.label}`
              : `${agreed ? `${AGREED_TIES[agreed].icon} ` : ''}${TIE_LABELS[person.tie]}`}
            {person.here && <em className="person-here">● here</em>}
            {person.blocked && <em className="person-blocked">blocked</em>}
          </span>
          <span className="person-closeness" aria-label={`Closeness ${person.closeness} of ${CLOSENESS_MAX}`}>
            <i style={{ width: `${(person.closeness / CLOSENESS_MAX) * 100}%` }} />
          </span>
        </div>
      </div>
      {person.pending && !person.blocked && (
        <p className="person-pending">
          {person.pending.askedByMe ? (
            <>
              {pendingKin
                ? `You asked ${person.firstName} to be your ${pendingKin.label.toLowerCase()}.`
                : `You asked to be ${person.pending.tie === 'partner' ? 'partners' : 'family'}.`}{' '}
              Waiting for {person.firstName}.
              <button
                type="button"
                className="house-button quiet"
                disabled={busy}
                onClick={() => act({ action: 'end_tie' })}
              >
                Take it back
              </button>
            </>
          ) : (
            <>
              {pendingKin
                ? `${person.firstName} asked to be family: they would be your ${pendingKin.label.toLowerCase()}.`
                : `${person.firstName} asked to be ${person.pending.tie === 'partner' ? 'your partner' : 'family'}.`}
              <button
                type="button"
                className="house-button save"
                disabled={busy}
                onClick={() => act({ action: 'accept' })}
              >
                Yes
              </button>
              <button
                type="button"
                className="house-button quiet"
                disabled={busy}
                onClick={() => act({ action: 'decline' })}
              >
                No
              </button>
            </>
          )}
        </p>
      )}
      {!person.blocked && (
        <div className="person-actions" aria-label={`Things to do with ${person.firstName}`}>
          {onChat && (
            <button type="button" className="house-button active" onClick={() => onChat(person.lifeId)}>
              💬 Chat
            </button>
          )}
          {SOCIAL_ACTION_KEYS.map((key: SocialActionKey) => {
            const rule = SOCIAL_ACTIONS[key];
            return (
              <button
                key={key}
                type="button"
                className="house-button"
                disabled={busy || !person.here}
                title={
                  person.here
                    ? `You ${effectsOf(rule.me)} · them ${effectsOf(rule.them)}`
                    : `${person.firstName} is not here`
                }
                onClick={() => act({ action: 'social', kind: key })}
              >
                {rule.icon} {rule.label}
              </button>
            );
          })}
        </div>
      )}
      {!compact && !person.blocked && (
        <div className="person-ties">
          {!agreed &&
            !person.pending &&
            (Object.keys(AGREED_TIES) as AgreedTie[]).map((tie) => (
              <button
                key={tie}
                type="button"
                className="house-button quiet"
                disabled={busy || person.closeness < AGREED_TIES[tie].needs}
                title={
                  person.closeness < AGREED_TIES[tie].needs
                    ? `Needs closeness ${AGREED_TIES[tie].needs} (you are at ${person.closeness})`
                    : undefined
                }
                aria-expanded={tie === 'family' ? choosingKin : undefined}
                onClick={() => (tie === 'family' ? setChoosingKin(!choosingKin) : act({ action: 'ask', tie }))}
              >
                {AGREED_TIES[tie].icon} Ask to be {tie === 'partner' ? 'partners' : 'family…'}
              </button>
            ))}
          {!agreed && !person.pending && choosingKin && (
            <div className="person-kin" role="group" aria-label={`What ${person.firstName} would be to you`}>
              {kinLabels
                .filter((item) => item.visible)
                .map((item) => (
                  <button
                    key={item.kind}
                    type="button"
                    className="house-button"
                    disabled={busy}
                    onClick={() => (setChoosingKin(false), act({ action: 'ask', tie: 'family', kin: item.kind }))}
                  >
                    {item.emoji} {item.label}
                  </button>
                ))}
            </div>
          )}
          {agreed && confirm !== 'end' && (
            <button type="button" className="house-button quiet" disabled={busy} onClick={() => setConfirm('end')}>
              End {agreed === 'partner' ? 'partnership' : 'family tie'}
            </button>
          )}
          {confirm === 'end' && (
            <span className="house-confirm">
              <small>Really?</small>
              <button
                type="button"
                className="house-button save"
                disabled={busy}
                onClick={() => (setConfirm(null), act({ action: 'end_tie' }))}
              >
                Yes, end it
              </button>
              <button type="button" className="house-button quiet" onClick={() => setConfirm(null)}>
                No
              </button>
            </span>
          )}
        </div>
      )}
      <div className="person-safety">
        {person.blocked ? (
          <button
            type="button"
            className="house-button quiet"
            disabled={busy}
            onClick={() => act({ action: 'unblock' })}
          >
            Unblock
          </button>
        ) : confirm === 'block' ? (
          <span className="house-confirm">
            <small>Block {person.firstName}? You will not see each other.</small>
            <button
              type="button"
              className="house-button save"
              disabled={busy}
              onClick={() => (setConfirm(null), act({ action: 'block' }))}
            >
              Yes, block
            </button>
            <button type="button" className="house-button quiet" onClick={() => setConfirm(null)}>
              No
            </button>
          </span>
        ) : (
          <button type="button" className="house-button quiet" disabled={busy} onClick={() => setConfirm('block')}>
            Block
          </button>
        )}
      </div>
    </li>
  );
}
